/* eslint-disable react/prop-types, react/jsx-handler-names */

import React from "react";
import PropTypes from "prop-types";
import Select, { components as selectComponents } from "react-select";
import AsyncSelect from "react-select/async";

import { withStyles } from "@mui/styles";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import { Check } from "@mui/icons-material";

import { connect } from "react-redux";
import { selectStyles } from "../../Helpers";
import { Trans as Translate } from "react-i18next";
import MenuItem from "../common/MenuItem";
import SearchField from "../common/SearchField";

/* ---------------------- STYLES ---------------------- */

const styles = (theme) => ({
  root: {
    flexGrow: 1,
  },
  input: {
    display: "flex",
  },
  valueContainer: {
    display: "flex",
    flex: 1,
    alignItems: "center",
    overflow: "hidden",
    paddingLeft: theme.spacing(1),
    minWidth: 0,
    position: "relative",
  },
  valueText: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    width: "100%",
    display: "block",
  },
  noOptionsMessage: {
    padding: `${theme.spacing(1)}px ${theme.spacing(1) * 2}px`,
  },
  singleValue: {
    fontSize: "inherit",
    color: "inherit",
  },
  paper: {
    position: "absolute",
    marginTop: theme.spacing(1),
    left: 0,
    right: 0,
  },
});

/* ---------------------- COMPONENTS ---------------------- */

function NoOptionsMessage(props) {
  return (
    <Typography
      color="textSecondary"
      className={props.selectProps.classes.noOptionsMessage}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}

/* ---------- FIXED: inputComponent now forces flex layout so indicators sit on the right ---------- */
function inputComponent({ inputRef, ...props }) {
  return (
    <div
      ref={inputRef}
      style={{
        display: "flex",
        alignItems: "center",
        flex: 1,
        minWidth: 0,
        width: "100%",
      }}
      {...props}
    />
  );
}

/* ---------- PLACEHOLDER FIX (IMPORTANT) ---------- */
function Placeholder(props) {
  const { hasValue, inputValue } = props.selectProps;
  if (hasValue || inputValue) return null;

  return (
    <Typography
      {...props.innerProps}
      style={{
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        left: 12,
        fontSize: 12,
        color: "#999",
        opacity: 0.7,
        pointerEvents: "none",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {props.children}
    </Typography>
  );
}

/* ---------------------- CONTROL ---------------------- */

function Control(props) {
  const disabledClass = props.selectProps.isDisabled ? "disabled" : "";

  if (props.selectProps.fill) {
    return (
      <SearchField
        fullWidth
        disabled={props.selectProps.isDisabled}
        InputLabelProps={{
          shrink: true,
          ...(props.selectProps.textFieldProps?.InputLabelProps || {}),
        }}
        InputProps={{
          inputComponent,
          inputProps: {
            readOnly: props.selectProps.readOnly,
            className: `theme-input-select ${disabledClass}`,
            inputRef: props.innerRef,
            children: props.children,
            ...props.innerProps,
          },
        }}
        {...props.selectProps.textFieldProps}
      />
    );
  }

  return (
    <TextField
      fullWidth
      variant="outlined"
      disabled={props.selectProps.isDisabled}
      sx={{
        "& .MuiOutlinedInput-input": {
          padding: "5px", // reduced padding (icons still have their own padding)
        },
      }}
      InputLabelProps={{
        shrink: true,
        ...(props.selectProps.textFieldProps?.InputLabelProps || {}),
      }}
      InputProps={{
        inputComponent,
        classes: {
          root: "theme-input-select",
          focused: "theme-cssFocused",
          notchedOutline: "theme-notchedOutline",
        },
        inputProps: {
          readOnly: props.selectProps.readOnly,
          className: `theme-input-select ${disabledClass}`,
          inputRef: props.innerRef,
          children: props.children,
          ...props.innerProps,
        },
      }}
      {...props.selectProps.textFieldProps}
    />
  );
}

/* ---------------------- OPTION ---------------------- */

function Option(props) {
  return (
    <MenuItem
      buttonRef={props.innerRef}
      selected={props.isFocused}
      component="div"
      sx={{
        fontWeight: props.isSelected ? 600 : 400,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: "white",
        paddingTop: 0,
        paddingBottom: 0,
        marginTop: 0,
        color: "#525252",
        "&:hover": {
          color: "white",
        },
      }}
      {...props.innerProps}
    >
      {props.children}
      {props.isSelected && (
        <Check fontSize="small" style={{ color: "inherit", marginLeft: 8 }} />
      )}
    </MenuItem>
  );
}

/* ---------------------- SINGLE VALUE ---------------------- */

function SingleValue(props) {
  return (
    <Typography
      className={props.selectProps.classes.singleValue}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}

/* ---------------------- VALUE CONTAINER ---------------------- */

function ValueContainer(props) {
  const { children, getValue, hasValue, selectProps } = props;

  if (!selectProps.isMulti) {
    return <div className={selectProps.classes.valueContainer}>{children}</div>;
  }

  const selected = getValue() || [];
  const maxVisible = 3;
  const visibleItems = selected.slice(0, maxVisible);
  const remaining = selected.length - maxVisible;

  const displayText =
    visibleItems
      .map((item) => item?.label || item?.name || item?.value || item?.key)
      .filter(Boolean)
      .join(", ") + (remaining > 0 ? ` +${remaining}` : "");
  const isSearching = Boolean(selectProps.inputValue);

  return (
    <div
      className={selectProps.classes.valueContainer}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        minWidth: 0,
      }}
    >
      <div
        style={{
          display: "flex",
          flex: 1,
          minWidth: 0,
          opacity: hasValue && selected.length > 0 && !isSearching ? 0 : 1,
        }}
      >
        {children}
      </div>

      {hasValue && selected.length > 0 && !isSearching && (
        <span
          className={selectProps.classes.valueText}
          title={displayText}
          style={{
            position: "absolute",
            left: 8,
            right: 30,
            pointerEvents: "none",
            fontSize: 14,
            color: "#666",
          }}
        >
          {displayText}
        </span>
      )}
    </div>
  );
}

function MenuList(props) {
  const { children, getValue, options, selectProps } = props;

  if (!selectProps.enableSelectAll || !options?.length) {
    return (
      <selectComponents.MenuList {...props}>
        {children}
      </selectComponents.MenuList>
    );
  }

  const selected = getValue() || [];
  const getValueKey = (option) => selectProps.getOptionValue(option);
  const allSelected =
    options.length > 0 &&
    options.every((option) =>
      selected.some((item) => getValueKey(item) === getValueKey(option)),
    );

  const handleSelectAll = (event) => {
    event.preventDefault();
    event.stopPropagation();
    selectProps.onChange(allSelected ? [] : [...options]);
  };

  return (
    <selectComponents.MenuList {...props}>
      <MenuItem
        component="div"
        onMouseDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onClick={handleSelectAll}
        sx={{
          fontWeight: 600,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          // color: '#525252',
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        {selectProps.translate?.("selectAll") || "Select All"}
        {allSelected && <Check fontSize="small" style={{ marginLeft: 8 }} />}
      </MenuItem>
      {children}
    </selectComponents.MenuList>
  );
}

/* ---------------------- MENU ---------------------- */

function Menu(props) {
  return (
    <Paper
      square
      className={props.selectProps.classes.paper}
      style={{ zIndex: 2 }}
      {...props.innerProps}
    >
      {props.children}
    </Paper>
  );
}

/* ---------------------- COMPONENTS MAP ---------------------- */

const components = {
  Control,
  Menu,
  MenuList,
  NoOptionsMessage,
  Option,
  SingleValue,
  ValueContainer,
  Placeholder,
  MultiValue: () => null,
  MultiValueLabel: () => null,
  MultiValueRemove: () => null,
  // DropdownIndicator & ClearIndicator are deliberately NOT overridden → react-select default icons will now be visible
};

/* ---------------------- MAIN COMPONENT ---------------------- */

class MultiSelect extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      [props.selectName || "multi"]: props.value || [],
    };

    this.suggestionCache = {
      source: null,
      translate: null,
      result: [],
    };
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.value !== this.props.value ||
      prevProps.selectName !== this.props.selectName
    ) {
      this.setState({
        [this.props.selectName || "multi"]: this.props.value || [],
      });
    }
  }

  handleChange = (value) => {
    const name = this.props.selectName || "multi";
    const index = this.props.index || 0;
    const safeValue = Array.isArray(value) ? value : [];

    this.setState({ [name]: safeValue }, () => {
      if (this.props.handleChange) {
        this.props.handleChange(name, safeValue, index);
      }

      if (this.props.returnSelected) {
        this.props.returnSelected(safeValue, "POST");
      }
    });
  };

  normalizeOption = (item) => {
    if (!item) return null;
    if (typeof item === "string" || typeof item === "number") {
      return {
        id: item,
        value: item,
        label: String(item),
      };
    }

    const label = item.label || item.name || item.value || item.key;
    const id = item.id || item.key || item.value || label;

    return {
      ...item,
      id,
      value: item.value || id,
      label: label ? String(label) : "",
    };
  };

  normalizeValue = (value) => {
    if (!Array.isArray(value)) return [];
    return value.map(this.normalizeOption).filter(Boolean);
  };

  mapValueFromSuggestions = (value, suggestions) => {
    const normalized = this.normalizeValue(value);
    if (!normalized.length) return [];

    const suggestionMap = new Map(
      suggestions.map((item) => [String(item.id), item]),
    );

    return normalized.map((item) => {
      const matched = suggestionMap.get(String(item.id));
      return matched || item;
    });
  };

  getSuggestions = (array, translate) => {
    if (
      this.suggestionCache.source === array &&
      this.suggestionCache.translate === translate
    ) {
      return this.suggestionCache.result;
    }

    const suggestions =
      array
        ?.map((v) => {
          const normalized = this.normalizeOption(v);
          if (!normalized) return null;
          const translatedLabel = translate
            ? translate(normalized.label)
            : normalized.label;

          return {
            ...normalized,
            label: translatedLabel,
          };
        })
        .filter(Boolean) || [];

    this.suggestionCache = {
      source: array,
      translate,
      result: suggestions,
    };

    return suggestions;
  };

  render() {
    const {
      array,
      themecolors,
      translate,
      isClearable,
      isSearchable,
      isLoading,
      isRtl,
      readOnly,
      enableSelectAll,
    } = this.props;

    const multiStyle = {
      control: (base) => ({
        ...base,
        minHeight: 38,
        alignItems: "center",
      }),
      menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
      }),
      menu: (base) => ({
        ...base,
        zIndex: 9999,
      }),
      valueContainer: (base) => ({
        ...base,
        paddingTop: 0,
        paddingBottom: 0,
        display: "flex",
        alignItems: "center",
      }),

      indicatorsContainer: (base) => ({
        ...base,
        alignItems: "center",
        paddingRight: 8,
      }),

      dropdownIndicator: (base, state) => ({
        ...base,
        padding: 4,
        color: "lightGray",
        "&:hover": {
          color: "inherit",
        },
      }),

      clearIndicator: (base, state) => ({
        ...base,
        padding: 4,
        color: "lightGray",
        "&:hover": {
          color: "inherit",
        },
      }),

      input: (base) => ({
        ...base,
        margin: 0,
        paddingTop: 0,
        paddingBottom: 0,
      }),
    };

    const suggestions = this.getSuggestions(array, translate);

    const value = this.mapValueFromSuggestions(
      this.state[this.props.selectName || "multi"] || [],
      suggestions,
    );

    const defaultStyles = selectStyles(themecolors);
    const mergedStyles = {
      ...defaultStyles,
      ...multiStyle,
      ...this.props.styles,
    };

    const commonProps = {
      ...this.props,
      menuPortalTarget: document.body,
      menuPosition: "fixed",
      isMulti: true,
      closeMenuOnSelect: false,
      blurInputOnSelect: false,
      menuShouldCloseOnBlur: true,
      hideSelectedOptions: false,
      controlShouldRenderValue: true,
      enableSelectAll,
      translate,
      isDisabled: readOnly,
      readOnly,
      isLoading,
      isClearable,
      isRtl,
      isSearchable,
      value,
      components,
      styles: mergedStyles,
      onChange: this.handleChange,
      filterOption: this.props.serverPaginated ? () => true : undefined,
      onInputChange: this.props.serverPaginated
        ? (inputValue, meta) => {
            if (meta.action === "input-change" && this.props.onSearchChange) {
              this.props.onSearchChange(inputValue);
            }
          }
        : undefined,
      onMenuScrollToBottom: this.props.onMenuScrollToBottom,
      placeholder: this.props.label || <Translate i18nKey="Select" />,
      textFieldProps: {
        ...(this.props.textFieldProps || {}),
      },
      getOptionLabel: (option) =>
        option?.label || option?.name || String(option?.value || ""),
      getOptionValue: (option) =>
        String(
          option?.id || option?.key || option?.value || option?.label || "",
        ),
      theme: (theme) => ({
        ...theme,
        colors: {
          ...theme.colors,
          neutral0: themecolors.backgroundColor,
          neutral5: themecolors.backgroundColor,
          neutral10: themecolors.backgroundColor,
          neutral20: themecolors.backgroundColor,
          neutral30: themecolors.textColor,
          neutral40: themecolors.textColor,
          neutral50: themecolors.textColor,
          neutral60: themecolors.textColor,
          neutral70: themecolors.textColor,
          neutral80: themecolors.textColor,
          neutral90: themecolors.textColor,
          dangerLight: themecolors.themeLightColor,
          danger: themecolors.themeLightInverse,
          primary75: themecolors.themeLightColor,
          primary50: themecolors.themeLightColor,
          primary25: themecolors.themeLightColor,
          primary: themecolors.themeLightColor,
        },
      }),
    };

    return (
      <div style={{ zIndex: 2, position: "relative" }}>
        {!this.props.async ? (
          <Select {...commonProps} options={suggestions} />
        ) : (
          <AsyncSelect
            {...commonProps}
            defaultOptions={suggestions}
            loadOptions={this.props.loadOptions}
          />
        )}
      </div>
    );
  }
}

MultiSelect.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  themecolors: state.themecolors,
});

export default connect(mapStateToProps)(
  withStyles(styles, { withTheme: true })(MultiSelect),
);
